import { Home, Files, FileDown, Mail, BarChart2, FileSearch, ClipboardList, ShieldAlert, ListChecks, MapPin, Users } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
} from "@/components/ui/sidebar";
import { getIcon } from "@/hooks/use-categories";
import { CATEGORY_SLUGS, isPlannerOnlyCategory } from "@shared/constants/categories";
import { PRODUCT_NAME } from "@shared/brand";
import { useExecutorOnboarding } from "@/pages/executor/ExecutorOnboardingPage";

interface UnifiedCategory {
  id: string;
  slug: string;
  name: string;
  icon: string;
  categoryType: string;
  sortOrder: number;
  completed: number;
  total: number;
}

interface ExecutorSidebarProps {
  clientName?: string;
  clientId?: string;
}

export function ExecutorSidebar({ clientName = "Estate", clientId }: ExecutorSidebarProps) {
  const [location] = useLocation();

  const { data: unifiedCategories = [] } = useQuery<UnifiedCategory[]>({
    queryKey: ["/api/unified/progress", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const res = await fetch(`/api/unified/progress?clientId=${clientId}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientId,
  });

  // s231 EXECUTOR-ONBOARDING: "Before you start" reopens the answers as a summary (board: obTag).
  const { data: onboarding } = useExecutorOnboarding(clientId ?? "", !!clientId);

  const actionTools = [
    {
      title: "Before you start",
      url: clientId ? `/executor/${clientId}/start` : "#",
      icon: ListChecks,
      badge: onboarding && typeof onboarding.done === "boolean" ? (onboarding.done ? "done" : "2 questions") : null,
    },
    {
      title: "Dashboard",
      url: clientId ? `/executor/${clientId}/dashboard` : "/dashboard",
      icon: Home,
      badge: null as string | null,
    },
    {
      title: "Task List",
      // Repointed (§1.6) to the canonical task list. The old /checklist route and
      // EstateChecklistPage were retired in s229 (CHECKLIST-RETIRE-1, D-229.53).
      url: clientId ? `/executor/${clientId}/tasks` : "#",
      icon: ClipboardList,
      // s126 Rider 2 (BADGE-TASKLIST-1): the stale badge counted the LEGACY checklist while
      // decorating /tasks — removed (do not rebind to the task summary).
      badge: null,
    },
    {
      // s237 (D-237.73, board v477): Contacts under Settle the estate, right after the task list — the planner's own page,
      // opened in place, so a number is to hand while a task is worked. After a verified death (PR D part 2).
      title: "Contacts",
      url: clientId ? `/executor/${clientId}/contacts` : "#",
      icon: Users,
      badge: null,
    },
    {
      title: "Estate Documents",
      url: clientId ? `/executor/${clientId}/estate-documents` : "#",
      icon: Files,
      badge: null,
    },
    {
      title: "Ontario Forms Export",
      url: "/export/ontario-forms",
      icon: FileDown,
      badge: null,
    },
    {
      title: "Letter Generator",
      url: clientId ? `/executor/${clientId}/letters` : "#",
      icon: Mail,
      badge: null,
    },
    {
      title: "Financial Summary",
      url: clientId ? `/executor/${clientId}/estate-summary` : "#",
      icon: BarChart2,
      badge: null,
    },
    {
      title: "Statement Scanner",
      url: clientId ? `/executor/${clientId}/statement-scanner` : "#",
      icon: FileSearch,
      badge: null,
    },
  ];

  const viewTools = [
    {
      // s237 PR D part 1 (D-236.50-25(b)): the planner's Where things are, the executor's view of it — this sidebar only
      // mounts once the death is verified (ExecutorDashboard's full-estate view).
      title: "Where things are",
      url: clientId ? `/executor/${clientId}/where` : "#",
      icon: MapPin,
    },
    {
      title: "Risk Profile",
      url: clientId ? `/executor/${clientId}/risk-profile` : "#",
      icon: ShieldAlert,
    },
  ];

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{PRODUCT_NAME}</h2>
          <p className="text-sm text-muted-foreground">{clientName}</p>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {/* Section 1: Settle the Estate — executor action tools */}
        <SidebarGroup>
          <SidebarGroupLabel>Settle the Estate</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {actionTools.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild isActive={location === item.url}>
                    <Link href={item.url}>
                      <item.icon className="h-4 w-4 text-primary" />
                      <span>{item.title}</span>
                      {item.badge && (
                        <span
                          className="ml-auto text-xs font-semibold rounded-full px-1.5 py-0.5 shrink-0"
                          style={{ backgroundColor: 'hsl(168 76% 42% / 0.15)', color: '#1ABC9C' }}
                        >
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Section 2: View Estate Plan — read-only reference */}
        <SidebarGroup>
          <SidebarGroupLabel>View Estate Plan</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {viewTools.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild isActive={location === item.url}>
                    <Link href={item.url}>
                      <item.icon className="h-4 w-4 text-primary" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {/* s171 F-S170.1 — the executor's nav advertised EVERY unified category, including
                  planner-only ones it cannot render. `/api/unified/progress` applies no role
                  filter (it returns the planner's whole category list), so the filter belongs
                  here, at the read-only surface. Reads the DS-1 declaration rather than testing a
                  slug, so a second planner-only category needs no edit in this file. */}
              {/* s237 (D-237.63, Reade): the classic Where Is Everything leaves the executor's nav — "Where things are"
                  covers it. Nav only: the page itself still opens from its address. */}
              {unifiedCategories.filter((cat) => !isPlannerOnlyCategory(cat.slug) && cat.slug !== CATEGORY_SLUGS.WHERE_IS_EVERYTHING).map((cat) => {
                const url = `/category/${cat.slug}`;
                const Icon = getIcon(cat.icon);
                return (
                  <SidebarMenuItem key={cat.id}>
                    <SidebarMenuButton asChild isActive={location === url}>
                      <Link href={url}>
                        <Icon className="h-4 w-4 text-primary" />
                        <span>{cat.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
